import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import NiceModal from '@ebay/nice-modal-react';
import { useShallow } from 'zustand/react/shallow';
import _ from 'lodash';

// Import hooks
import { useAppSelector } from 'store/hooks';
import { useAuth, useProtectedPage } from 'components/utilities';
import { useSummaryStore, useCalculatedPrice, useSelectPacket } from 'components/views/offer_configurator/hooks';
import { useConfiguratorContext } from 'components/context/configurator/ConfiguratorProvider';

// Import routes
import { CONFIGURATOR_PATHS } from 'components/routes/configurator/routes';

// Import components
import { MainPacketChangeModal } from 'components/views/offer_configurator/components/common/modals';

// Import helpers
import { getActionType } from 'components/views/offer_configurator/helpers/packets';
import { sendPacketAddToCartEvent } from 'helpers/tracking';

type Props = {
	type: 'review' | 'modal';
	closeModal?: () => void;
};

export const useMainPackets = ({ type, closeModal = () => {} }: Props) => {
	const history = useHistory();
	const [main, setMain] = useSummaryStore(useShallow((state) => [state.main, state.setMain]));
	const setProtectedPage = useProtectedPage((state) => state.setProtectedPage);
	const { isAuth } = useAuth();
	const packets = useAppSelector(({ configurator }) => configurator.packets.main);

	const calculated = useCalculatedPrice();

	const { subscription } = useConfiguratorContext();

	const isModal = type === 'modal';

	useEffect(() => {
		const unsubscribe = useSummaryStore.subscribe(
			({ main, premium }) => ({ main, premium }),
			() => {
				if (isModal) return closeModal();
				const pathname = CONFIGURATOR_PATHS.SUMMARY;
				!isAuth && setProtectedPage({ pathname });
				history.push(pathname);
			},
			{
				fireImmediately: false,
				equalityFn: (prev, next) => {
					const isStoreCleared = !next.main && !next.premium.length;
					return isStoreCleared || _.isEqual(prev, next);
				}
			}
		);

		return () => unsubscribe();
	}, []);

	const { onSelect } = useSelectPacket(({ uuid, price_id, title, subtype }) => {
		const price = calculated.find((product) => product.uuid === uuid);
		const action = getActionType(subscription, price);
		const setPacket = () => {
			sendPacketAddToCartEvent({ item_id: uuid, price: price?.current_price, subtype, item_name: title });
			setMain({ uuid, price_id }, action);
		};

		if (subscription && !isModal) {
			NiceModal.show(MainPacketChangeModal, {
				currentPacket: subscription.main.name,
				newPacket: title,
				setPacket
			});
		} else {
			setPacket();
		}
	});

	const initialSlide = packets.findIndex(({ uuid }) => (isModal ? uuid === main?.uuid : uuid === subscription?.main.product_uuid));

	const isAnyPromotion = calculated.some((priceInfo) => priceInfo.promotion?.id);

	return {
		main,
		packets,
		initialSlide,
		subscription,
		onSelect,
		isAnyPromotion,
		calculated
	};
};
